import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
} from "antd";
import {
  FilterOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getWarningsStatistics } from "../../../api/statistics/statisticsRequest";
import { getZones } from "../../../api/zone/zoneRequest";
import { getTypeInfraction } from "../../../api/typeInfraction/infractionType";
import { statusTextWarning } from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const WarningsStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedtypeInfractions, setSelectedTypeInfractions] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);

  const [zones, setZones] = useState([]);
  const [typeInfractions, setTypeInfractions] = useState([]);
  const [warningsData, setWarningsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchZones();
    fetchTypeInfractions();
  }, []);

  const filterWarningsStatistics = async () => {
    setLoading(true);

    try {
      const data = await getWarningsStatistics({
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        zone_id: selectedZone,
        type_infraction: selectedtypeInfractions,
        group_by: selectedGroupBy,
      });
      setWarningsData(data); // Update state with the fetched data
    } catch (error) {
      setWarningsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    setLoading(true);
    try {
      const data = await getZones();
      setZones(data.data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des zones");
    } finally {
      setLoading(false);
    }
  };

  const fetchTypeInfractions = async () => {
    setLoading(true);
    try {
      const data = await getTypeInfraction();
      setTypeInfractions(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des types d'infractions");
    } finally {
      setLoading(false);
    }
  };

  const pieChartOptions = {
    chart: {
      type: "pie",
    },
    title: {
      text: "Avertissements par Type",
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      pointFormat: "Nombre: <b>{point.y}</b>",
    },
    series: [
      {
        name: "Avertissements",
        data: warningsData
          ? warningsData.by_type.map((item) => ({
              name: item.name,
              y: item.count,
            }))
          : [],
      },
    ],
  };

  const timelineChartOptions = {
    chart: {
      type: "column",
      zoomType: "x",
    },
    title: {
      text: "Chronologie des Avertissements",
    },
    xAxis: {
      categories: warningsData
        ? warningsData.timeline.map((item) => item.date)
        : [],
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Nombre",
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Avertissements Totaux",
        data: warningsData
          ? warningsData.timeline.map((item) => item.count)
          : [],
      },
    ],
  };

  const zoneChartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Statistiques par Contrat",
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: warningsData?.by_zone.map((zone) => zone.name),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Nombre d'Avertissements",
      },
    },
    tooltip: {
      shared: true,
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      backgroundColor: "rgba(255,255,255,0.8)",
      borderColor: "#CCC",
      borderWidth: 1,
      shadow: false,
    },
    series: [
      {
        name: "Nombre d'Avertissements",
        data: warningsData?.by_zone.map((zone) => zone.count),
      },
    ],
  };

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  placeholder={["Date début", "Date fin"]}
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Sélectionner le type d'infractions"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedTypeInfractions(value)}
                  allowClear
                >
                  {typeInfractions.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Sélectionner la Contrat"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedZone(value)}
                  allowClear
                >
                  {zones.map((zone) => (
                    <Option key={zone.id} value={zone.id}>
                      {zone.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  placeholder="Groupé par"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedGroupBy(value)}
                  allowClear
                >
                  <Option value={"daily"}>Quotidien</Option>
                  <Option value={"weekly"}>Hebdomadaire</Option>
                  <Option value={"monthly"}>Mensuel</Option>
                </Select>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterWarningsStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {warningsData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des Avertissements
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title="Avertissements Totaux"
                    value={warningsData.summary.total}
                    prefix={<WarningOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title="Avertissements Finalisés"
                    value={warningsData.summary.total - warningsData.summary.cancelled_count}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Statistic
                    title="Avertissements Annulés"
                    value={warningsData.summary.cancelled_count}
                    prefix={<CloseCircleOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={pieChartOptions}
                  />
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={timelineChartOptions}
                  />
                </Card>
              </Col>
            </Row>

            <Card className="shadow-sm">
              <Title level={3} className="mb-4">
                Avertissements par Statut
              </Title>
              <Table
                dataSource={warningsData.by_status}
                columns={[
                  {
                    title: "Statut",
                    dataIndex: "status",
                    key: "status",
                    render: (text) => statusTextWarning(text),
                  },
                  {
                    title: "Nombre",
                    dataIndex: "count",
                    key: "count",
                    align: "center",
                    sorter: (a, b) => a.count - b.count,
                  },
                ]}
                pagination={false}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Statistiques par Contrat
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={zoneChartOptions}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails des Statistiques par Contrat
              </Title>
              <Table
                dataSource={warningsData?.by_zone}
                columns={[
                  { title: "Contrat", dataIndex: "name", key: "name" },
                  {
                    title: "Nombre",
                    dataIndex: "count",
                    key: "count",
                    align: "center",
                    sorter: (a, b) => a.count - b.count,
                  },
                ]}
                pagination={false}
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default WarningsStatistics;